<template>
  <div class="col-12 col-xl-12">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="row align-items-center">
            <div class="col-md-6">
              <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
                {{ $t('reward_points.add.title') }}
              </h6>
            </div>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center ms-3">
          <div class="col-12">
            <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
              <div class="row g-3">
                <!-- Name -->
                <div class="col-md-6">
                  <label for="name" class="bmd-label-float">{{ $t('reward_points.name') }}</label>
                  <input
                    type="text"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.name }"
                    id="name"
                    v-model.trim="formData.name"
                    required
                    @blur="validateField('name')"
                  />
                  <div class="invalid-feedback">{{ errors.name }}</div>
                </div>

                <!-- Description -->
                <div class="col-md-6">
                  <label for="description" class="bmd-label-float">{{ $t('reward_points.description') }}</label>
                  <input
                    type="text"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.description }"
                    id="description"
                    v-model.trim="formData.description"
                    @blur="validateField('description')"
                  />
                  <div class="invalid-feedback">{{ errors.description }}</div>
                </div>

                <!-- Type -->
                <div class="col-md-6">
                  <label for="type" class="bmd-label-float">{{ $t('reward_points.type') }}</label>
                  <select
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.type }"
                    id="type"
                    v-model="formData.type"
                    required
                    @blur="validateField('type')"
                  >
                    <option value="" disabled>{{ $t('reward_points.selectType') }}</option>
                    <option value="positive">{{ $t('reward_points.positive') }}</option>
                    <option value="negative">{{ $t('reward_points.negative') }}</option>
                  </select>
                  <div class="invalid-feedback">{{ errors.type }}</div>
                </div>

                <!-- Points -->
                <div class="col-md-6">
                  <label for="points" class="bmd-label-float">{{ $t('reward_points.points') }}</label>
                  <input
                    type="number"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.points }"
                    id="points"
                    v-model.number="formData.points"
                    required
                    min="1"
                    @blur="validateField('points')"
                  />
                  <div class="invalid-feedback">{{ errors.points }}</div>
                </div>

                <!-- Icon -->
                <div class="col-md-6">
                  <label for="icon" class="bmd-label-float">{{ $t('reward_points.icon') }}</label>
                  <div class="input-group">
                    <select
                      class="form-select borderless-input"
                      :class="{ 'is-invalid': errors.icon }"
                      id="icon"
                      v-model="formData.icon"
                      @blur="validateField('icon')"
                    >
                      <option value="">{{ $t('reward_points.selectIcon') }}</option>
                      <option v-for="icon in translatedIcons" :key="icon.value" :value="icon.value">
                        {{ icon.label }}
                      </option>
                    </select>
                    <span class="input-group-text bg-transparent border-0" v-if="formData.icon">
                      <i :class="`fas ${formData.icon} text-${formData.type === 'positive' ? 'success' : 'danger'}`"></i>
                    </span>
                  </div>
                  <div class="invalid-feedback">{{ errors.icon }}</div>
                </div>

             
                <!-- Icon Preview -->
                <div class="col-12 mt-3" v-if="formData.icon">
                  <div class="card bg-light">
                    <div class="card-body text-center">
                      <i :class="`fas ${formData.icon} fa-3x text-${formData.type === 'positive' ? 'success' : 'danger'} mb-2`"></i>
                      <p class="text-muted mb-0">{{ getIconLabel(formData.icon) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex gap-3 mt-4">
                <button 
                  type="button" 
                  class="btn btn-primary btn-sm flex-grow-1"
                  @click="handleSubmit"
                  :disabled="loading"
                >
                  <span v-if="loading">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ $t('reward_points.adding') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-plus ms-2"></i>
                    {{ $t('reward_points.create') }}
                  </span>
                </button>
                <button 
                  type="button" 
                  class="btn btn-outline-secondary btn-sm flex-grow-1"
                  @click="handleCancel"
                  :disabled="loading"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ $t('common.cancel') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/api'

const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const loading = ref(false)
const errors = ref({})

const iconList = [
  'fa-star',
  'fa-thumbs-up',
  'fa-award',
  'fa-trophy',
  'fa-medal',
  'fa-check-circle',
  'fa-hand-paper',
  'fa-hands-helping',
  'fa-heart',
  'fa-smile',
  'fa-certificate',
  'fa-gem',
  'fa-crown',
  'fa-flag',
  'fa-bolt',
  'fa-fire',
  'fa-sun',
  'fa-moon',
  'fa-cloud',
  'fa-umbrella',
  'fa-snowflake',
  'fa-leaf',
  'fa-tree',
  'fa-seedling',
  'fa-feather',
  'fa-dove',
  'fa-fish',
  'fa-hippo',
  'fa-dragon',
  'fa-ghost',
  'fa-robot',
  'fa-user-astronaut',
  'fa-rocket',
  'fa-satellite',
  'fa-ufo',
  'fa-book',
  'fa-pen',
  'fa-pencil-alt',
  'fa-highlighter',
  'fa-marker',
  'fa-stamp',
  'fa-eraser',
  'fa-paperclip',
  'fa-link',
  'fa-chain',
  'fa-lock',
  'fa-unlock',
  'fa-key',
  'fa-wrench',
  'fa-hammer',
  'fa-screwdriver',
  'fa-tools',
  'fa-cog',
  'fa-cogs',
  'fa-sliders-h',
  'fa-filter',
  'fa-funnel-dollar',
  'fa-briefcase',
  'fa-suitcase',
  'fa-suitcase-rolling',
  'fa-address-book',
  'fa-address-card',
  'fa-id-card',
  'fa-id-badge',
  'fa-passport',
  'fa-globe',
  'fa-globe-americas',
  'fa-globe-europe',
  'fa-globe-asia',
  'fa-map',
  'fa-map-marked',
  'fa-map-marked-alt',
  'fa-map-pin',
  'fa-thumbtack'
]

// Computed property for translated icons
const translatedIcons = computed(() => {
  return iconList.map(icon => ({
    value: icon,
    label: t(`icons.${icon}`) || icon // Fallback to icon name if translation not found
  }))
})

// Helper function to get icon label
const getIconLabel = (iconValue) => {
  const icon = translatedIcons.value.find(i => i.value === iconValue)
  return icon ? icon.label : iconValue
}

const formData = ref({
  name: '',
  description: '',
  type: '',
  icon: '',
  points: 1,
  is_active: true
})

const validateField = (field) => {
  const REQUIRED_MSG = t('validation.required')

  if (field === 'name' && !formData.value.name.trim()) {
    errors.value.name = REQUIRED_MSG
    return false
  }

  if (field === 'type' && !formData.value.type) {
    errors.value.type = REQUIRED_MSG
    return false
  }

  if (field === 'points' && (!formData.value.points || formData.value.points < 1)) {
    errors.value.points = t('validation.min_value', { value: 1 })
    return false
  }

  delete errors.value[field]
  return true
}

const validateForm = () => {
  errors.value = {}
  const validations = [
    validateField('name'),
    validateField('type'),
    validateField('points')
  ]

  return validations.every(valid => valid)
}

const setErrors = (serverErrors) => {
  errors.value = { ...serverErrors }
}

const handleSubmit = async () => {
  if (!validateForm()) {
    toast.warning(t('validation.correctErrors'))
    return
  }

  loading.value = true

  try {
    await api.post('/reward-points', formData.value)
    
    toast.success(t('reward_points.messages.created'))
    router.push({ name: 'settings.reward-points.index' })
    
  } catch (error) {
    console.error('Error creating reward point:', error)
    
    if (error.response?.status === 422) {
      setErrors(error.response.data.errors)
      for (const field in error.response.data.errors) {
        toast.error(error.response.data.errors[field][0])
      }
    } else {
      toast.error(error.response?.data?.message || t('errors.save_failed'))
    }
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  if (!loading.value) {
    router.push({ name: 'settings.reward-points.index' })
  }
}
</script>